import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { getLatestServices, getHotServices, getCategoryById, formatViews } from '@/lib/content';
import ServiceCard from '@/components/base/ServiceCard';
import SectionHeader from '@/components/base/SectionHeader';
import HeroSection from './components/HeroSection';
import FocusCarousel from './components/FocusCarousel';
import CategoryQuickNav from './components/CategoryQuickNav';

export default function Home() {
  useSeo({
    title: '在线-多元化在线服务平台，满足您的各类需求',
    description: '在线网站提供丰富多样的在线服务信息，涵盖教育、娱乐、购物等多个领域，助力用户快速找到所需资源，实现便捷在线体验。',
    keywords: '在线,在线服务,在线平台,在线查询,在线资源',
  });

  const latest = getLatestServices(8);
  const hot = getHotServices(6);

  return (
    <>
      <HeroSection />
      <FocusCarousel />
      <CategoryQuickNav />

      <section className="w-full px-4 md:px-8 py-10 md:py-14">
        <SectionHeader
          title="最新上线"
          subtitle="实时更新各分类最新服务信息"
          moreTo="/categories"
          moreText="全部分类"
          accentIcon="ri-time-line"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
          {latest.map((item) => (
            <ServiceCard key={item.id} service={item} />
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
        <SectionHeader
          title="热门精选"
          subtitle="依据用户活跃度综合排序，聚焦高关注度内容"
          moreTo="/search?sort=hot"
          moreText="查看更多"
          accentIcon="ri-fire-line"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {hot.map((item, i) => {
            const category = getCategoryById(item.categoryId);
            return (
              <Link
                key={item.id}
                to={`/detail/${item.id}`}
                className="group flex gap-4 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-16 h-16 shrink-0 flex flex-col items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <span className="text-xl font-bold leading-none">{i + 1}</span>
                  <span className="text-[10px] mt-0.5">排行</span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {item.title}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                    <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800">{category?.name}</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(item.views)}</span>
                    <span className="flex items-center gap-1 text-accent-600"><i className="ri-star-fill"></i>{item.rating}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs md:text-sm mb-4">
              <i className="ri-information-line"></i>
              关于在线
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
              让每一条在线服务信息<br className="hidden md:block" />都能被快速找到
            </h2>
            <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
              在线是一个多元化综合信息服务平台，面向追求高效与信息多样性的广泛网民，聚合教育学习、娱乐休闲、购物优惠、生活服务、健康医疗、职场提升、科技数码、旅游出行等多类别在线服务信息。平台以信息聚合、分类浏览、精准筛选与内容详情浏览为核心，帮助你在海量信息中快速定位目标资源。
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: 'ri-folders-line', title: '分类清晰', desc: '八大类目层级分明' },
                { icon: 'ri-filter-3-line', title: '筛选精准', desc: '多维条件一键组合' },
                { icon: 'ri-search-eye-line', title: '查找高效', desc: '关键词模糊匹配' },
              ].map((f) => (
                <div key={f.title} className="p-4 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-lg mb-2">
                    <i className={f.icon}></i>
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-900">{f.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{f.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/about" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-md border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-chat-smile-2-line"></i>
                联系反馈
              </Link>
            </div>
          </div>

          <div className="relative w-full h-[300px] md:h-[420px] rounded-lg overflow-hidden bg-background-100">
            <img
              src="https://readdy.ai/api/search-image?query=Minimal%20modern%20illustration%20of%20people%20browsing%20online%20services%20on%20devices%2C%20organized%20category%20cards%20and%20search%20elements%2C%20calm%20blue%20white%20warm%20grey%20tones%2C%20clean%20professional%20portal%20feel&width=900&height=760&seq=online-about-intro&orientation=portrait"
              alt="在线平台信息聚合示意"
              title="在线平台 - 信息聚合与分类浏览"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute bottom-4 left-4 right-4 p-4 rounded-lg bg-background-50/95 border border-background-200">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xl">
                  <i className="ri-shield-check-line"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">清晰的信息层级</p>
                  <p className="text-xs text-foreground-500">分类 · 筛选 · 详情，一步到位</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}